"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import Tabs, { TAB_STYLE, TabItem } from "@/components/common/Tab";
import Image from "next/image";
import PageTitle from "@/components/common/PageTitle";

export default function CareersLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();

  // Check if we're on a JD detail page (not the main careers page)
  const isDetailPage = pathname !== "/company/careers" && pathname.startsWith("/company/careers/");

  // If not on a detail page, just render children (company layout will handle it)
  if (!isDetailPage) {
    return <>{children}</>;
  }

  const tabs: TabItem[] = [
    {
      id: "1",
      name: "Frontend Developer",
      value: "/company/careers/frontend-developer",
      href: "/company/careers/frontend-developer",
    },
    {
      id: "2",
      name: "PHP Developer",
      value: "/company/careers/php-developer",
      href: "/company/careers/php-developer",
    },
    {
      id: "3",
      name: "Exploit Developer",
      value: "/company/careers/exploit-developer",
      href: "/company/careers/exploit-developer",
    },
    {
      id: "4",
      name: "Offensive Security Engineer",
      value: "/company/careers/offensive-security-engineer",
      href: "/company/careers/offensive-security-engineer",
    },
    {
      id: "5",
      name: "Mobile Application Pentest",
      value: "/company/careers/mobile-application-pentest",
      href: "/company/careers/mobile-application-pentest",
    },
    {
      id: "6",
      name: "Web Security Consultant",
      value: "/company/careers/web-security-consultant",
      href: "/company/careers/web-security-consultant",
    },
  ];

  // Determine the title based on the current path
  const getTitle = () => {
    if (pathname.includes("frontend-developer")) return "Frontend Developer";
    if (pathname.includes("php-developer")) return "PHP Developer";
    if (pathname.includes("exploit-developer")) return "Exploit Developer";
    if (pathname.includes("offensive-security-engineer")) return "Offensive Security Engineer";
    if (pathname.includes("mobile-application-pentest")) return "Mobile Application Pentest";
    if (pathname.includes("web-security-consultant")) return "Web Security Consultant";
    return "Careers";
  };

  return (
    <div className="relative min-h-screen flex flex-col bg-background pb-16 sm:pb-[120px]">
      {/* Background Image - Fixed height, maintains aspect ratio */}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-screen pointer-events-none">
        <Image
          src="/images/subpage-background.svg"
          alt=""
          fill
          className="object-cover object-top opacity-20"
        />
      </div>

      <div className="relative z-10 flex flex-col flex-1">
        {/* Breadcrumb — same treatment as /service, /service/defend and the
            Resources advisories: a red rule fills the left gutter and the
            segments sit past a transparent 5px bar so the text lines up with
            the title accent bar below. Header is fixed (h-72): 72px clearance
            + ~56px gap. */}
        <div className="w-full px-4 sm:px-6 lg:px-8 pt-[112px] md:pt-[128px] overflow-hidden">
          <div className="relative max-w-[1300px] mx-auto">
            <span
              aria-hidden
              className="pointer-events-none absolute top-1/2 right-full -translate-y-1/2 h-[2px] w-screen bg-primary"
            />
            <nav
              aria-label="Breadcrumb"
              className="flex flex-wrap items-center gap-1 border-l-[5px] border-transparent pl-4 md:pl-6 text-sm tracking-[0.35px] text-white"
            >
              {/* /company has no page of its own, so the root segment points at
                  the first Company tab. */}
              <Link
                href="/company/about-us"
                className="transition-colors hover:text-text-default"
              >
                Company
              </Link>
              <span>/</span>
              <Link
                href="/company/careers"
                className="transition-colors hover:text-text-default"
              >
                Careers
              </Link>
            </nav>
          </div>
        </div>

        {/* Title and Tab Section */}
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <section className="w-full max-w-[1300px] mx-auto pb-6 pt-8 md:pb-[48px]">
            {/* Title above the tab row, as on every other tabbed header
                (products, service, defend, resources, company, contact,
                ot-security). This page used to have the two the other way round,
                which read as the job title labelling the page content rather than
                naming the tab you are on.

                mobileLines=2: "Offensive Security Engineer" and "Mobile
                Application Pentest" both wrap at 390px. */}
            <PageTitle className="mb-4 md:mb-6" mobileLines={2}>
              {getTitle()}
            </PageTitle>

            {/* Tab Navigation */}
            <Tabs
              tabs={tabs}
              currentValue={pathname}
              style={TAB_STYLE.STYLE_1}
              showContent={false}
              useRouter={true}
              customClassName="[&>a:first-child]:!pl-0"
            />
          </section>
        </div>

        {/* Page Content */}
        <main className="flex-1 w-full">{children}</main>
      </div>
    </div>
  );
}
